import { Helmet } from 'react-helmet-async';
import { SEOMetaProps, SchemaMarkup } from '@/types/seo';
import { seoConfig } from '@/config/seo';
import { generateSchemaMarkup } from '@/utils/schema';

interface SEOHeadProps extends SEOMetaProps {
  children?: React.ReactNode;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  keywords = [],
  canonicalUrl,
  ogImage,
  schemaType = 'WebPage',
  breadcrumbs = [],
  author,
  publishDate,
  modifiedDate,
  noIndex = false,
  noFollow = false,
  children,
}) => {
  // Generate full title with site name
  const fullTitle = title.includes(seoConfig.site.name) 
    ? title 
    : `${title} | ${seoConfig.site.name}`;

  // Use provided canonical URL or generate from current page
  const canonical = canonicalUrl || (() => {
    if (typeof window !== 'undefined') {
      // For localhost, use production URL structure
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.')) {
        return `${seoConfig.site.url}${window.location.pathname}${window.location.search}`;
      }
      // For production, use actual URL without trailing slash
      return window.location.href.replace(/\/$/, '');
    }
    return seoConfig.site.url;
  })();

  // Use provided OG image or default
  const ogImageUrl = ogImage || seoConfig.defaultMeta.ogImage;

  // Combine provided keywords with default keywords
  const allKeywords = [...new Set([...keywords, ...seoConfig.defaultMeta.keywords])];

  // Generate robots meta content
  const robotsContent = [
    noIndex ? 'noindex' : 'index',
    noFollow ? 'nofollow' : 'follow',
    'max-image-preview:large',
    'max-snippet:-1',
    'max-video-preview:-1'
  ].join(', ');

  // Generate schema markup
  const schemaMarkup: SchemaMarkup[] = generateSchemaMarkup({
    type: schemaType,
    title,
    description,
    url: canonical,
    image: ogImageUrl,
    breadcrumbs,
    author,
    publishDate,
    modifiedDate,
  });

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {allKeywords.length > 0 && (
        <meta name="keywords" content={allKeywords.join(', ')} />
      )}
      <meta name="author" content={author || seoConfig.organization.name} />
      <link rel="canonical" href={canonical} />

      {/* Robots Meta */}
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={schemaType === 'Article' ? 'article' : 'website'} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={seoConfig.site.name} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seoConfig.site.social.twitter} />
      <meta name="twitter:creator" content={seoConfig.site.social.twitter} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />
      <meta name="twitter:image:alt" content={`${seoConfig.site.name} - ${title}`} />

      {/* Article-specific meta tags */}
      {schemaType === 'Article' && publishDate && (
        <meta property="article:published_time" content={publishDate} />
      )}
      {schemaType === 'Article' && modifiedDate && (
        <meta property="article:modified_time" content={modifiedDate} />
      )}
      {schemaType === 'Article' && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Additional SEO Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={seoConfig.site.name} />
      <meta name="application-name" content={seoConfig.site.name} />
      <meta name="msapplication-TileColor" content="#1e40af" />
      <meta name="theme-color" content="#1e40af" />

      {/* Schema.org JSON-LD */}
      {schemaMarkup.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}

      {/* Additional custom head content */}
      {children}
    </Helmet>
  );
};